/**
 * Client pour les routes équipes / matchs / annonces (espace compétition).
 *
 * Toutes les fonctions renvoient `data.data` (déballé de l'enveloppe
 * `{ success, data }` produite par le backend Neo) ou lèvent une erreur.
 */

import { BACKEND_URL } from '../config';

export class TeamsApiError extends Error {
  status: number;
  code?: string;

  constructor(status: number, code?: string, message?: string) {
    super(message || code || `HTTP ${status}`);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(
  path: string,
  options: { method?: string; token?: string; body?: unknown } = {},
): Promise<T> {
  const { method = 'GET', token, body } = options;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${BACKEND_URL}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const json = await response.json().catch(() => null);
  if (!response.ok || (json && json.success === false)) {
    const code = json?.error as string | undefined;
    throw new TeamsApiError(response.status, code, json?.message || code);
  }
  return (json?.data ?? json) as T;
}

export interface TeamMember {
  pseudo: string;
  pseudo_display?: string;
  role: string;
}

export interface TeamSearchDivision {
  id: number;
  nom: string;
  index: number;
  /** Classement dans la division (phase en cours), si connu */
  classement?: number | null;
}

export interface TeamSearchItem {
  id: number;
  nom: string;
  date?: string | null; // Date de création de l'équipe (ancienneté)
  members: string[];
  membersDetailed: TeamMember[]; // Capitaine + lieutenant en tête, avec rôle
  membersDisplay: string;
  effectif: number;
  complet: boolean;
  division: TeamSearchDivision | null; // Division actuelle au championnat par équipes, si inscrite
  photoUrl: string;
}

export type TeamSortOption = 'name' | 'division' | 'members_desc' | 'members_asc' | 'age_asc' | 'age_desc';

export interface NextDeadline {
  id: number;
  rdv: string;
  etat: string;
  nbparties: number;
  adversaire: number;
}

export interface TeamSlots {
  capitaine: string;
  lieutenant: string;
  attaquant: string;
  def_E: string;
  def_N: string;
  def_O: string;
  rempl_1: string;
  rempl_2: string;
  rempl_3: string;
  rempl_4: string;
}

export interface MyTeam {
  id: number;
  nom: string;
  devise?: string;
  photoUrl?: string;
  members: TeamMember[];
  slots: TeamSlots;
  is_captain: boolean;
  is_lieutenant: boolean;
  is_staff: boolean;
  next_deadline: NextDeadline | null;
  contacts: Record<string, unknown> | null;
}

export interface MercatoEntry {
  id: number;
  equipe: number;
  joueur: string;
  joueur_display: string;
  initiative: 'joueur' | 'equipe';
  message: string;
  reponse: string | null;
  date: string;
  etat: 'attente' | 'ok' | 'refus' | 'abandon';
}

export interface MatchItem {
  id: number;
  equipe1: number;
  equipe2: number;
  equipe1_nom?: string | null;
  equipe2_nom?: string | null;
  rdv: string;
  etat: string;
  nbparties: number;
  surcontrats: number;
  att_tournant: number;
  idcompetition: number | null;
  quand: string;
  pm1: number | null;
  pm2: number | null;
  /** Division au championnat par équipes dans laquelle ce match s'est joué (matchs de championnat uniquement) */
  division?: { id: number; nom: string; index: number } | null;
  lineup1: Record<string, unknown> | null;
  lineup2: Record<string, unknown> | null;
}

export function searchTeams(params: {
  search?: string; complete?: boolean; champ?: boolean; played?: boolean;
  sort?: TeamSortOption; limit?: number; offset?: number; token?: string;
}): Promise<{ teams: TeamSearchItem[]; total: number }> {
  const q = new URLSearchParams();
  if (params.search) q.set('search', params.search);
  if (params.complete) q.set('complete', 'true');
  if (params.champ) q.set('champ', 'true');
  if (params.played) q.set('played', 'true');
  if (params.sort) q.set('sort', params.sort);
  if (params.limit) q.set('limit', String(params.limit));
  if (params.offset) q.set('offset', String(params.offset));
  return request(`/api/teams/directory?${q.toString()}`, { token: params.token });
}

export function getMyTeam(token: string): Promise<{ team: MyTeam | null }> {
  return request('/api/teams/me', { token });
}

export function createTeam(token: string, nom: string, devise?: string): Promise<{ id: number; nom: string }> {
  return request('/api/teams', { method: 'POST', token, body: { nom, devise } });
}

export function updateTeam(token: string, teamId: number, body: { nom?: string; devise?: string }) {
  return request(`/api/teams/${teamId}`, { method: 'PUT', token, body });
}

export function leaveTeam(token: string, teamId: number) {
  return request(`/api/teams/${teamId}/leave`, { method: 'POST', token, body: {} });
}

export function expelMember(token: string, teamId: number, pseudo: string) {
  return request(`/api/teams/${teamId}/members/${encodeURIComponent(pseudo)}/expel`, {
    method: 'POST', token, body: {},
  });
}

export function setLieutenant(token: string, teamId: number, pseudo: string | null) {
  return request(`/api/teams/${teamId}/lieutenant`, { method: 'POST', token, body: { pseudo: pseudo || '' } });
}

export function transferCaptain(token: string, teamId: number, pseudo: string) {
  return request(`/api/teams/${teamId}/captain`, { method: 'POST', token, body: { pseudo } });
}

export function setRoles(token: string, teamId: number, roles: Record<string, string>) {
  return request(`/api/teams/${teamId}/roles`, { method: 'POST', token, body: roles });
}

export function getMercato(token: string, teamId: number, etat?: string): Promise<{ items: MercatoEntry[] }> {
  const q = etat ? `?etat=${encodeURIComponent(etat)}` : '';
  return request(`/api/teams/${teamId}/mercato${q}`, { token });
}

export function createMercato(
  token: string, teamId: number, initiative: 'joueur' | 'equipe', joueur: string, message?: string,
) {
  return request(`/api/teams/${teamId}/mercato`, { method: 'POST', token, body: { initiative, joueur, message } });
}

export function mercatoAction(
  token: string, teamId: number, mercatoId: number, action: 'accept' | 'reject' | 'cancel',
) {
  return request(`/api/teams/${teamId}/mercato/${mercatoId}/${action}`, { method: 'POST', token, body: {} });
}

export function getMatches(params: {
  team_id?: number; opponent_team_id?: number; player?: string; etat?: string; kind?: string;
  limit?: number; offset?: number; token?: string;
}): Promise<{ items: MatchItem[]; count: number }> {
  const q = new URLSearchParams();
  if (params.team_id) q.set('team_id', String(params.team_id));
  if (params.opponent_team_id) q.set('opponent_team_id', String(params.opponent_team_id));
  if (params.player) q.set('player', params.player);
  if (params.etat) q.set('etat', params.etat);
  if (params.kind) q.set('kind', params.kind);
  if (params.limit) q.set('limit', String(params.limit));
  if (params.offset) q.set('offset', String(params.offset));
  return request(`/api/matches?${q.toString()}`, { token: params.token });
}

export function getMatchHistory(params: {
  team_id?: number; opponent_team_id?: number; player?: string; etat?: string; kind?: string;
  limit?: number; offset?: number; token?: string;
}): Promise<{ items: MatchItem[]; count: number }> {
  const q = new URLSearchParams();
  if (params.team_id) q.set('team_id', String(params.team_id));
  if (params.opponent_team_id) q.set('opponent_team_id', String(params.opponent_team_id));
  if (params.player) q.set('player', params.player);
  if (params.etat) q.set('etat', params.etat);
  if (params.kind) q.set('kind', params.kind);
  if (params.limit) q.set('limit', String(params.limit));
  if (params.offset) q.set('offset', String(params.offset));
  return request(`/api/matches/history?${q.toString()}`, { token: params.token });
}

export function createMatch(token: string, body: {
  opponent_team_id: number; rdv: string; nbparties?: number; surcontrats?: boolean; att_tournant?: boolean;
}) {
  return request('/api/matches', { method: 'POST', token, body });
}

export function matchAction(token: string, matchId: number, action: 'accept' | 'reject' | 'cancel') {
  return request(`/api/matches/${matchId}/${action}`, { method: 'POST', token, body: {} });
}

export function setLineup(token: string, matchId: number, body: {
  attaquant: string; def_E: string; def_N: string; def_O: string; capitaine_defi?: string;
}) {
  return request(`/api/matches/${matchId}/lineup`, { method: 'POST', token, body });
}

export function getLineup(token: string, matchId: number) {
  return request(`/api/matches/${matchId}/lineup`, { token });
}

export function setMatchCaptain(token: string, matchId: number, capitaineDefi: string) {
  return request(`/api/matches/${matchId}/captain`, { method: 'PUT', token, body: { capitaine_defi: capitaineDefi } });
}

// ─── Petites annonces ─────────────────────────────────────────────

export interface AnnonceAuthor {
  author_type: 'team' | 'player';
  team_id?: number;
  team_nom?: string | null;
  pseudo?: string;
  pseudo_display?: string;
}

export interface Annonce {
  id: number;
  pseudo: string;
  pseudo_display?: string;
  titre: string;
  contenu: string;
  quand: string;
  etat: string;
  annonce: 'equipe' | 'joueur' | 'defi';
  rdv: string;
  nbparties: number;
  surcontrats: number;
  att_tournant: number;
  reply_count?: number;
  author?: AnnonceAuthor;
}

export function getAnnonces(params: { annonce?: string; state?: string; q?: string; limit?: number; offset?: number }) {
  const q = new URLSearchParams();
  if (params.annonce) q.set('annonce', params.annonce);
  if (params.state) q.set('state', params.state);
  if (params.q) q.set('q', params.q);
  if (params.limit) q.set('limit', String(params.limit));
  if (params.offset) q.set('offset', String(params.offset));
  return request<{ items: Annonce[]; total: number }>(`/api/forums/annonces?${q.toString()}`);
}

export function getAnnonceDetail(id: number) {
  return request<{ thread: Annonce; replies: Annonce[] }>(`/api/forums/annonces/${id}`);
}

export function createAnnonce(token: string, body: {
  titre: string; contenu: string; annonce?: 'equipe' | 'joueur' | 'defi';
  rdv?: string; nbparties?: number; surcontrats?: boolean; att_tournant?: boolean;
}) {
  return request('/api/forums/annonces', { method: 'POST', token, body });
}

export function replyAnnonce(token: string, id: number, contenu: string, titre?: string) {
  return request(`/api/forums/annonces/${id}/replies`, { method: 'POST', token, body: { titre, contenu } });
}

export function postulerAnnonce(token: string, id: number, message?: string) {
  return request(`/api/forums/annonces/${id}/postuler`, { method: 'POST', token, body: { message } });
}

export function inviterAnnonce(token: string, id: number, message?: string) {
  return request(`/api/forums/annonces/${id}/inviter`, { method: 'POST', token, body: { message } });
}

export function fixerRdvAnnonce(token: string, id: number, body: {
  rdv?: string; nbparties?: number; surcontrats?: boolean; att_tournant?: boolean;
}) {
  return request(`/api/forums/annonces/${id}/fixer-rdv`, { method: 'POST', token, body });
}

export function cloturerAnnonce(token: string, id: number) {
  return request(`/api/forums/annonces/${id}/cloturer`, { method: 'POST', token, body: {} });
}
